import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/site/Bits";
import { Reveal } from "@/components/site/Reveal";
import { Check, ArrowUpRight, ArrowRight, Nfc as NfcIcon, ChevronLeft, ChevronRight, Palette, Info, Wand2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const SOCIAL_IMAGES = [
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bbr-nouvelle-version/artifacts/1ynj4st6_Capture%20d%E2%80%99e%CC%81cran%202026-08-10%20a%CC%80%2011.03.41.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bbr-nouvelle-version/artifacts/93jdhy3t_Capture%20d%E2%80%99e%CC%81cran%202026-08-10%20a%CC%80%2000.27.48.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bbr-nouvelle-version/artifacts/ydvic07a_Capture%20d%E2%80%99e%CC%81cran%202026-08-10%20a%CC%80%2000.27.30.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bbr-nouvelle-version/artifacts/z45rz4yk_Gemini_Generated_Image_y06s0yy06s0yy06s.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bbr-nouvelle-version/artifacts/i04qlyh2_Gemini_Generated_Image_uifmvbuifmvbuifm.png",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_bbr-nouvelle-version/artifacts/yx9nr55u_Gemini_Generated_Image_45hdc45hdc45hdc4.png",
];

const PRODUCTS = [
  { key: "social", price: 10, images: SOCIAL_IMAGES },
  { key: "badge", price: 20, images: [] },
  { key: "card", price: 25, images: [] },
];

function TalkBtn({ testid, full }) {
  const { t } = useI18n();
  return (
    <Link to="/contact" data-testid={testid}
      className={`${full ? "w-full" : ""} inline-flex items-center justify-center gap-2 bg-brand-gradient text-white font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity duration-300`}>
      {t("buttons.talkProject")} <ArrowUpRight className="w-4 h-4" />
    </Link>
  );
}

function NfcCarousel() {
  const { t } = useI18n();
  const [active, setActive] = useState(0);
  const [img, setImg] = useState(0);
  const [dir, setDir] = useState(1);
  const product = PRODUCTS[active];
  const feats = t(`nfc.${product.key}_features`);
  const desc = t(`nfc.${product.key}_desc`);
  const select = (i) => { setActive(i); setImg(0); setDir(1); };
  const goto = (i) => { setDir(i > img ? 1 : -1); setImg(i); };
  const prev = () => { setDir(-1); setImg((v) => (v - 1 + product.images.length) % product.images.length); };
  const next = () => { setDir(1); setImg((v) => (v + 1) % product.images.length); };

  // Défilement automatique (5 s par slide) — le minuteur repart à zéro à chaque changement d'image (auto ou manuel)
  useEffect(() => {
    if (product.images.length <= 1) return undefined;
    const id = setInterval(() => { setDir(1); setImg((v) => (v + 1) % product.images.length); }, 5000);
    return () => clearInterval(id);
  }, [active, img, product.images.length]);

  return (
    <div className="mt-10" data-testid="nfc-carousel">
      <div className="flex flex-wrap justify-center gap-2.5">
        {PRODUCTS.map((p, i) => (
          <button key={p.key} onClick={() => select(i)} data-testid={`nfc-product-${p.key}`}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${i === active ? "bg-brand-gradient text-white" : "glass text-zinc-300 hover:bg-white/10"}`}>
            {t(`nfc.${p.key}_name`)}
          </button>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
        <div>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-square flex items-center justify-center bg-[#0a0a12]">
            {product.images.length ? (
              <>
                <AnimatePresence initial={false} custom={dir} mode="popLayout">
                  <motion.img
                    key={img}
                    src={product.images[img]}
                    alt={t(`nfc.${product.key}_name`)}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="nfc-image"
                    custom={dir}
                    variants={{
                      enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
                      center: { x: 0, opacity: 1 },
                      exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0 }),
                    }}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
                  />
                </AnimatePresence>
                {product.images.length > 1 && (
                  <>
                    <button onClick={prev} data-testid="nfc-prev" aria-label="prev"
                      className="absolute z-10 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition-colors duration-300"><ChevronLeft className="w-5 h-5" /></button>
                    <button onClick={next} data-testid="nfc-next" aria-label="next"
                      className="absolute z-10 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-black/70 transition-colors duration-300"><ChevronRight className="w-5 h-5" /></button>
                  </>
                )}
              </>
            ) : (
              <div className="text-center px-6">
                <span className="w-16 h-16 rounded-2xl bg-brand-gradient flex items-center justify-center mx-auto"><NfcIcon className="w-8 h-8 text-white" /></span>
                <p className="mt-4 text-sm text-zinc-400 max-w-[220px]">{t("nfc.visualsSoon")}</p>
              </div>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              {product.images.map((_, i) => (
                <button key={i} onClick={() => goto(i)} data-testid={`nfc-dot-${i}`} aria-label={`image ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === img ? "w-6 bg-cyan-400" : "w-2 bg-white/25 hover:bg-white/50"}`} />
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-display font-bold text-2xl text-white">{t(`nfc.${product.key}_name`)}</h3>
          <p className="mt-2 font-display font-black text-4xl text-gradient">{product.price} € <span className="text-base font-normal text-zinc-500">{t("nfc.ttc")} {t("nfc.perUnit")}</span></p>
          {desc && <p className="mt-4 text-zinc-400">{desc}</p>}
          <ul className="mt-5 space-y-2.5 text-sm">
            {(Array.isArray(feats) ? feats : []).map((f) => (
              <li key={f} className="flex gap-2 text-zinc-300"><Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> {f}</li>
            ))}
          </ul>
          {product.key === "social" && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/40 px-4 py-2.5" data-testid="nfc-no-sub">
              <Check className="w-5 h-5 text-emerald-400 shrink-0" />
              <span className="font-semibold text-emerald-300">{t("nfc.social_noSub")}</span>
            </div>
          )}
          {product.key === "social" && (
            <p className="mt-4 flex items-start gap-2 text-sm text-cyan-300"><Palette className="w-4 h-4 shrink-0 mt-0.5" /> {t("nfc.colorNote")}</p>
          )}
          <div className="mt-7"><TalkBtn testid={`nfc-cta-${product.key}`} /></div>
        </div>
      </div>
    </div>
  );
}

export default function Nfc() {
  const { t } = useI18n();
  const chips = t("nfc.persoChips");

  return (
    <div>
      <PageHero eyebrow={t("nfc.eyebrow")} title={t("nfc.title")} subtitle={t("nfc.subtitle")}>
        <p className="mt-6 text-zinc-400 max-w-2xl">{t("nfc.intro")}</p>
        <div className="mt-6"><TalkBtn testid="nfc-hero-cta" /></div>
      </PageHero>

      {/* PRODUITS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="nfc-products">
        <p className="text-center text-sm text-cyan-400">{t("nfc.billedSeparately")}</p>
        <NfcCarousel />
        <p className="mt-10 text-center text-xs text-zinc-500 max-w-2xl mx-auto">{t("nfc.pricesNote")}</p>
      </section>

      {/* À SAVOIR */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <Reveal>
          <div className="glass rounded-3xl p-8 sm:p-10" data-testid="nfc-know">
            <div className="flex items-center gap-2"><Info className="w-5 h-5 text-cyan-400" /><h2 className="font-display font-bold text-2xl text-white">{t("nfc.knowTitle")}</h2></div>
            <p className="mt-4 text-zinc-300 leading-relaxed">{t("nfc.knowText")}</p>
            <p className="mt-3 text-zinc-400 leading-relaxed">{t("nfc.knowText2")}</p>
          </div>
        </Reveal>
      </section>

      {/* PERSONNALISATION */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto" data-testid="nfc-perso">
            <span className="w-14 h-14 rounded-2xl bg-brand-gradient flex items-center justify-center mx-auto"><Wand2 className="w-7 h-7 text-white" /></span>
            <h2 className="mt-5 font-display font-bold text-3xl text-white">{t("nfc.persoTitle")}</h2>
            <p className="mt-4 text-zinc-400">{t("nfc.persoText")}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2.5">
              {(Array.isArray(chips) ? chips : []).map((c) => (
                <span key={c} className="px-4 py-2 rounded-full text-sm text-white/90 border border-white/10 bg-white/[0.04]">{c}</span>
              ))}
            </div>
            <p className="mt-6 text-sm text-cyan-300">{t("nfc.persoCustom")}</p>
          </div>
        </Reveal>
      </section>

      {/* LIEN VERS TARIFS */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <Reveal>
          <div className="rounded-[2rem] bg-brand-gradient p-8 sm:p-10 text-center" data-testid="nfc-to-pricing">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white">{t("nfc.linkTitle")}</h2>
            <p className="mt-3 text-white/90 max-w-2xl mx-auto">{t("nfc.linkText")}</p>
            <Link to="/tarifs" data-testid="nfc-pricing-link"
              className="mt-7 inline-flex items-center gap-2 bg-[#0a0a12] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#141420] transition-colors duration-300">
              {t("nfc.linkBtn")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
